// Back-office de l'exploitant : formes des réponses et requêtes.
//
// Rassemblé ici plutôt que dans `api/types.ts` et `api/queries.ts` : rien de
// tout cela ne concerne l'application que voient les clients.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export interface BoUser {
  id: string;
  name: string;
  email: string;
  orgRole: 'admin' | 'member';
  status: 'active' | 'invited' | 'disabled';
  createdAt: string;
  termsAcceptedAt: string | null;
  lastSeen: string | null;
}

export interface BoAccounts {
  totals: { organizations: number; users: number; newUsers7d: number; newUsers30d: number; pending: number };
  organizations: BoOrganization[];
}

/** `pending` : demande à approuver. `suspended` : accès coupé, données gardées. */
export type BoOrgStatus = 'pending' | 'active' | 'suspended';

export interface BoOrganization {
  id: string;
  name: string;
  createdAt: string;
  status: BoOrgStatus;
  approvedAt: string | null;
  users: BoUser[];
}

export const DATA_COLUMNS = [
  ['clients', 'Clients'],
  ['projects', 'Projets'],
  ['studies', 'Études'],
  ['interviews', 'Entretiens'],
  ['verbatims', 'Verbatims'],
  ['insights', 'Insights'],
  ['personas', 'Personas'],
  ['tests', 'Tests'],
  ['connectors', 'Connecteurs'],
] as const;

type DataKey = (typeof DATA_COLUMNS)[number][0];
type DataCounts = Record<DataKey, number> & { recordings: number; recordingBytes: number };

export interface BoData {
  totals: DataCounts;
  organizations: (DataCounts & { id: string; name: string; createdAt: string; lastSeen: string | null })[];
}

export interface BoUsageLine {
  key: string;
  label: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}

export interface BoAi {
  days: number;
  since: string | null;
  currency: string;
  total: Omit<BoUsageLine, 'key' | 'label'>;
  byOrganization: BoUsageLine[];
  byTask: BoUsageLine[];
  byModel: BoUsageLine[];
  byMonth: BoUsageLine[];
  unpricedModels: string[];
  /** Appels faits avec la clé d'IA du client : hors coût de l'exploitant. */
  ownKey: { calls: number; organizations: number; configured: number };
}

export interface BoPlatform {
  warnings: string[];
  database: { engine: string; host: string | null; role: string | null; bypassesRls: boolean | null; provider?: string };
  migrations: { applied: number; available: number; last: string | null };
  ai: {
    provider: string;
    configured: boolean;
    models: Record<string, string>;
    embeddings: string | null;
    transcription: boolean;
    location: string | null;
  };
  storage: { backend: string; bucket: string | null; recordingsEnabled: boolean };
  secretsReady: boolean;
  runtime: {
    environment: string;
    version: string;
    node: string;
    service: string | null;
    revision: string | null;
    gcpProject: string | null;
    uptimeSeconds: number;
  };
  links: { label: string; url: string }[];
  superAdmins: number;
  accessLog: { at: string; email: string; section: string }[];
}

const section = <T,>(name: string, query = '') =>
  ({
    queryKey: ['backoffice', name, query] as const,
    queryFn: () => api.get<T>(`/api/backoffice/${name}${query}`),
    // Un écran de supervision se relit à l'ouverture : pas de donnée tiède.
    staleTime: 0,
    retry: false,
  });

export const useBoAccounts = () => useQuery(section<BoAccounts>('accounts'));
export const useBoData = () => useQuery(section<BoData>('data'));
export const useBoAi = (days: number) => useQuery(section<BoAi>('ai', `?days=${days}`));
export const useBoPlatform = () => useQuery(section<BoPlatform>('platform'));

// --- Gestion des comptes -----------------------------------------------------
//
// Chaque geste renvoie la liste des comptes à jour : on la pose dans le cache
// plutôt que de la redemander.

const ACCOUNTS = ['backoffice', 'accounts', ''] as const;
const accountPath = (id: string) => `/api/backoffice/accounts/${encodeURIComponent(id)}`;

export type BoAccountAction = 'approve' | 'suspend' | 'reject';

export function useBoAccountAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: BoAccountAction }) =>
      action === 'reject'
        ? api.del<BoAccounts>(accountPath(id))
        : api.post<BoAccounts>(`${accountPath(id)}/${action}`, {}),
    onSuccess: (data) => qc.setQueryData(ACCOUNTS, data),
  });
}

export interface BoCreatedAccount {
  email: string;
  /** Donné UNE fois : le serveur ne le garde que haché. */
  temporaryPassword: string;
}

export function useBoCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { organizationName: string; name: string; email: string }) =>
      api.post<BoAccounts & { created: BoCreatedAccount }>('/api/backoffice/accounts', a),
    onSuccess: (data) => qc.setQueryData(ACCOUNTS, data),
  });
}

// --- Mode maintenance ---------------------------------------------------------

export interface BoMaintenance {
  active: boolean;
  message: string;
  until: string | null;
  /** Organisations qui gardent l'accès (en plus des super-administrateurs). */
  allowedOrgIds: string[];
  startedAt: string | null;
  startedBy: string | null;
}

export interface BoMaintenanceView {
  maintenance: BoMaintenance;
  organizations: { id: string; name: string; status: BoOrgStatus; emails: string[] }[];
}

export const useBoMaintenance = () => useQuery(section<BoMaintenanceView>('maintenance'));

export function useBoSaveMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: { active: boolean; message: string; until: string | null; allowedOrgIds: string[] }) =>
      api.put<{ maintenance: BoMaintenance }>('/api/backoffice/maintenance', m),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['backoffice'] });
      // L'état public et la session : le bandeau de l'exploitant suit aussitôt.
      void qc.invalidateQueries({ queryKey: ['maintenance'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// --- Clés et réglages ---------------------------------------------------------

export interface BoSetting {
  key: string;
  service: string;
  label: string;
  secret: boolean;
  editable: boolean;
  testable: boolean;
  choices: string[] | null;
  present: boolean;
  /** D'où vient la valeur en service : enregistrée ici, ou variable du serveur. */
  source: 'backoffice' | 'serveur' | null;
  /** Fin d'une clé (« …abcd »), valeur d'un réglage non secret, ou masque. Jamais une clé entière. */
  display: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  note: string | null;
}

interface BoKeys {
  settings: BoSetting[];
}

const KEYS = ['backoffice', 'keys', ''] as const;
const keyPath = (key: string) => `/api/backoffice/keys/${encodeURIComponent(key)}`;

export const useBoKeys = () => useQuery(section<BoKeys>('keys'));

export function useSetBoKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => api.put<BoKeys>(keyPath(key), { value }),
    onSuccess: (data) => qc.setQueryData(KEYS, data),
  });
}

export function useClearBoKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.del<BoKeys>(keyPath(key)),
    onSuccess: (data) => qc.setQueryData(KEYS, data),
  });
}

export function useTestBoKey() {
  return useMutation({
    mutationFn: (key: string) =>
      api.post<{ result: { ok: boolean; message: string } }>(`${keyPath(key)}/test`).then((b) => b.result),
  });
}
